import { useState } from "react";
import { useLang } from "../LanguageContext";
import { translations } from "../data/translations";
import { ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import ProductModal from "./ProductModal";

export default function ProductCard({ product, index = 0 }) {
  const { lang } = useLang();
  const t = translations[lang];
  const [modalOpen, setModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  const normalizedApplication =
    product.application === "power-supply" ? "power" : product.application;

  const appColors = {
    cloud: "bg-blue-100 text-blue-700",
    storage: "bg-emerald-100 text-emerald-700",
    edge: "bg-amber-100 text-amber-700",
    ai: "bg-purple-100 text-purple-700",
    power: "bg-rose-100 text-rose-700"
  };

  const appLabels = {
    cloud: t.products?.cloud || "Cloud",
    storage: t.products?.storage || "Storage",
    edge: t.products?.edge || "Edge",
    ai: t.products?.ai || "AI",
    power: t.products?.power || "Power"
  };

  const productImage = product.images?.[0] || product.imageUrl;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: index * 0.05 }}
        className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 border border-gray-100 overflow-hidden flex flex-col cursor-pointer group"
        onClick={() => setModalOpen(true)}
      >
        <div className="relative h-48 bg-gray-50 flex items-center justify-center overflow-hidden">
          {!imageError ? (
            <img
              loading="lazy"
              src={productImage}
              alt={product.name?.[lang] || product.name?.en || product.id}
              className="max-h-full max-w-full object-contain p-4 group-hover:scale-105 transition-transform duration-300"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm text-center px-4 flex-col">
              <span className="mb-2">FOTO</span>
              <span className="text-xs">{product.name?.[lang] || product.name?.en}</span>
            </div>
          )}

          <div className="absolute top-2 right-2 p-1.5 bg-white/80 rounded-md opacity-0 group-hover:opacity-100 transition hover:bg-white">
            <ExternalLink className="w-4 h-4 text-gray-600" />
          </div>
        </div>

        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-center justify-between mb-2 gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide line-clamp-1">
              {product.category}
            </span>

            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                appColors[normalizedApplication] || "bg-gray-100 text-gray-600"
              }`}
            >
              {appLabels[normalizedApplication] || product.application}
            </span>
          </div>

          <h3 className="text-lg font-bold text-gray-900 mb-2">
            {product.name?.[lang] || product.name?.en}
          </h3>

          <p className="text-sm text-gray-600 mb-3 flex-1 line-clamp-3">
            {product.description?.[lang] || product.description?.en}
          </p>

          <div className="text-xs text-gray-500 mb-3 font-mono bg-gray-50 px-2 py-1 rounded">
            {product.specs}
          </div>

          <div className="mt-auto pt-3 border-t flex items-center justify-between gap-3">
            <span className="text-brand-blue font-bold text-lg">
              {product.price
                ? `€${product.price}`
                : (t.products?.pricePlaceholder || "Anfragen")}
            </span>

            <span className="text-xs text-gray-400">ID: {product.id}</span>
          </div>
        </div>
      </motion.div>

      {modalOpen && (
        <ProductModal
          product={product}
          lang={lang}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}
